import React, { useEffect, useState } from "react";
import moment from "moment";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../layout/Layout";
import { cn } from "../../../utils/classnames";
import { getBooking } from "../../../actions/admin/booking";
import { BackIcon, CloseCircleIcon } from "../../icons";
import customAxios from "../../../utils/customAxios";
import CustomerInformation from "./_components/CustomerInformation";
import ComparisonTable from "./_components/ComparisonTable";
import CancellationFeeInformation from "./_components/CancellationFeeInformation";
import ApproveDraftModal from "./_components/ApproveDraftModal";
import DeleteDraftModal from "./_components/DeleteDraftModal";

// ─── Status helpers ────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  PENDING_APPROVAL: { label: "Change Requested", className: "tw:bg-[#20639B1A] tw:text-[#20639B]" },
  REJECTED: { label: "Change Rejected", className: "tw:bg-[#FFEAE7] tw:text-[#EA4335]" },
  APPROVED: { label: "Change Approved", className: "tw:bg-[#DBF4F1] tw:text-[#18B1A3]" },
  DRAFT: { label: "Draft", className: "tw:bg-[#F1F1F1] tw:text-[#8D8D8D]" },
  UPDATED_BY_ADMIN: { label: "Updated by Admin", className: "tw:bg-[#F5EEFF] tw:text-[#8F5CD1]" },
};

const getStatusMeta = (status, responseBy, isAdminEdit = false) => {
  const key = String(status || "").toUpperCase();
  if (key === "APPROVED" && !responseBy) return { label: "Updated", className: "tw:bg-[#F9EDD3] tw:text-[#A3780F]" };
  if (key === "APPROVED" && isAdminEdit) return STATUS_STYLES["UPDATED_BY_ADMIN"];
  return STATUS_STYLES[key] || { label: status || "-", className: "tw:bg-[#F1F1F1] tw:text-[#8D8D8D]" };
};

const formatDateTime = (value, withWeekday = false) => {
  if (!value) return "-";
  const m = moment(value);
  return withWeekday ? m.format("MMM. Do, YYYY (ddd) H:mm") : m.format("MMM. Do, YYYY H:mm");
};

// ─── Page ──────────────────────────────────────────────────────────────────────

const AdminModificationHistoryDetailPage = ({ match }) => {
  const bookingId = match?.params?.id;
  const requestId = match?.params?.requestId;
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { loading, booking } = useSelector((state) => state.booking);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  moment.locale("en"); // Ensure moment uses English locale for consistent date formatting

  useEffect(() => {
    if (!bookingId) return;
    dispatch(getBooking(bookingId));
  }, [dispatch, bookingId]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleApproveDraft = async (id) => {
    setIsActionLoading(true);
    try {
      await customAxios.post("/api/admin/request-change-booking/approve-draft", { requestId: id });
      dispatch(getBooking(bookingId));
      history.push(`/admin/bookinglist/edit/v2/${bookingId}/modification-history`);
    } catch (err) {
      console.error("Approve draft failed:", err);
    } finally {
      setIsActionLoading(false);
      setShowApproveModal(false);
    }
  };

  const handleDeleteDraft = async (id) => {
    setIsActionLoading(true);
    try {
      await customAxios.post("/api/request-change-booking/delete", { canceledRequestId: id, bookingId });
      dispatch(getBooking(bookingId));
      history.push(`/admin/bookinglist/edit/v2/${bookingId}/modification-history`);
    } catch (err) {
      console.error("Delete draft failed:", err);
    } finally {
      setIsActionLoading(false);
      setShowDeleteModal(false);
    }
  };

  // ── Resolve request ──────────────────────────────────────────────────────────

  const requestRows = Array.isArray(booking?.requestChangeBookings)
    ? booking.requestChangeBookings
    : Array.isArray(booking?.requestChangeBooking)
      ? booking.requestChangeBooking
      : [];

  const selectedRequest =
    requestRows.find((item) => String(item?._id) === String(requestId)) ||
    location?.state?.historyItem ||
    null;

  const statusValue = String(selectedRequest?.status || "PENDING_APPROVAL").toUpperCase();
  const statusMeta = getStatusMeta(statusValue, selectedRequest?.responseBy, selectedRequest?.isAdminEdit);
  const headerDate = formatDateTime(selectedRequest?.requestCreatedAt || selectedRequest?.date || selectedRequest?.createdAt);

  // ── Loading / not found ──────────────────────────────────────────────────────

  if (loading || !booking || !selectedRequest) {
    return (
      <Layout>
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 w-100" style={{ minHeight: "100vh" }}>
          <div className="tw:flex tw:items-center tw:justify-center tw:py-20 tw:text-[#8D8D8D]">
            {loading ? "Loading…" : "Modification request not found."}
          </div>
        </main>
      </Layout>
    );
  }

  // ── Customer information props ────────────────────────────────────────────────

  const bookingInfoList = Array.isArray(booking?.bookingInfo) ? booking.bookingInfo : [];
  const firstBookingInfo = bookingInfoList[0] || {};
  const isMultipleVehicle = bookingInfoList.length > 1;
  const vehiclePassengerRows = (bookingInfoList.length ? bookingInfoList : [firstBookingInfo]).map((item, index) => ({
    key: item?._id || `vehicle-row-${index}`,
    index,
    vehicleLabel: `Vehicle #${index + 1}`,
    vehicleName: item?.vehicleTypeName || item?.vehicleTypeID?.typename || "-",
  }));
  const passengerNameText = `${booking?.passengerPronounced || ""} ${booking?.passengerName || "-"}`.trim();
  const passengerPhoneText = booking?.passengerPrefixNumber && booking?.passengerPhoneNumber
    ? `(${booking.passengerPrefixNumber}) ${booking.passengerPhoneNumber}`
    : "-";

  const chargeAmount = Number(selectedRequest?.chargeAmount || 0);

  const currentJST = new Date(Date.now() + 9 * 60 * 60 * 1000);
  const isPastPickup = booking?.pickupDateTime && currentJST > new Date(booking.pickupDateTime);

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100">
        <div className="tw:font-hanken-grotesk tw:text-[#212529]">

          {/* ── Header ── */}
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6 tw:pb-[28px] tw:lg:pb-[42px] tw:px-5 tw:pt-8">
            <Link
              to={`/admin/bookinglist/edit/v2/${bookingId}/modification-history`}
              className="tw:inline-flex tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[#282828] tw:!no-underline"
            >
              <BackIcon color="#8D8D8D" width={7} height={10} />
              <span className="tw:!font-hanken-grotesk tw:text-[#282828] tw:text-[14px]">Back</span>
            </Link>

            <div className="tw:flex tw:flex-1 tw:items-center tw:justify-between tw:gap-4">
              <div className="tw:flex tw:flex-col">
                <div className="tw:!font-hanken-grotesk tw:font-[500] tw:text-[14px] tw:text-[#7c7b7b]">
                  {`[Cart #${booking?.cartID || ""}・Booking #${booking?.key || ""}]`}
                </div>
                <div className="tw:!font-hanken-grotesk tw:text-[24px] tw:font-[500] tw:leading-normal tw:text-[#212529]">
                  Booking Modification
                </div>
              </div>
              <span className={cn("tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-[5px] tw:px-6 tw:text-[15px] tw:leading-[22px]", statusMeta.className)}>
                {statusMeta.label}
              </span>
            </div>
          </div>

          {/* ── Customer Information ── */}
          <CustomerInformation
            isMultipleVehicle={isMultipleVehicle}
            vehiclePassengerRows={vehiclePassengerRows}
            passengerNameText={passengerNameText}
            passengerPhoneText={passengerPhoneText}
            booking={booking}
          />

          {/* ── Comparison Table ── */}
          <div className="tw:overflow-x-auto">
            <ComparisonTable
              selectedRequest={selectedRequest}
              booking={booking}
              statusValue={statusValue}
            />
          </div>

          {/* ── Cancellation Fee Information ── */}
          {(selectedRequest?.totalPriceCancellation > 0 || selectedRequest?.totalMeetAssistPriceCancellation > 0) && (
            <CancellationFeeInformation
              data={selectedRequest}
              meetAssistChangesIdentification={selectedRequest?.changesIdentification?.meetAssist}
            />
          )}

          {/* ── Draft action bar ── */}
          {(statusValue === "DRAFT" && !isPastPickup && booking.bookingStatus !== "CANCELLED") && (
            <div className="tw:mt-6 tw:flex tw:items-center tw:justify-between tw:gap-3">
              {/* Left: Edit + Approve */}
              <div className="tw:flex tw:items-center tw:gap-3">
                <Link
                  to={`/admin/booking-edit?bookingId=${bookingId}&requestId=${selectedRequest?._id}&from=modification-history`}
                  className="tw:!w-[300px] tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:!rounded-[5px] tw:!border tw:!border-[#D9D9D9] tw:!bg-[#F3F3F3] tw:px-5 tw:font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#282828] tw:!no-underline"
                >
                  Edit
                </Link>

                <button
                  type="button"
                  disabled={isActionLoading}
                  onClick={() => setShowApproveModal(true)}
                  className={`tw:!w-[300px] tw:inline-flex tw:h-[45px] tw:cursor-pointer tw:items-center tw:justify-center tw:!rounded-[5px] tw:px-5 tw:font-hanken-grotesk tw:!text-[15px] tw:!font-[700] tw:!text-white ${Number(selectedRequest?.chargeAmount || 0) > 0
                    ? "tw:!bg-[#3CAEA3]"
                    : "tw:!bg-[#DBBE78]"
                    }`}
                >
                  {Number(selectedRequest?.chargeAmount || 0) > 0
                    ? `¥ Charge ${Number(selectedRequest.chargeAmount).toLocaleString()} JPY`
                    : "Update Booking Details"}
                </button>
              </div>

              {/* Right: Delete Draft */}
              <button
                type="button"
                disabled={isActionLoading}
                onClick={() => setShowDeleteModal(true)}
                className="tw:!w-[150px] tw:!gap-2 tw:inline-flex tw:h-[45px] tw:cursor-pointer tw:items-center tw:justify-center tw:!rounded-[5px] tw:!border tw:!border-[#EA4335] tw:bg-white tw:px-5 tw:font-hanken-grotesk tw:!text-[15px] tw:!font-[400] tw:!text-[#EA4335]"
              >
                <CloseCircleIcon color="#EA4335" size={12} />
                Delete Draft
              </button>
            </div>
          )}
        </div>
      </main>

      <ApproveDraftModal
        show={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={() => handleApproveDraft(selectedRequest?._id)}
        isLoading={isActionLoading}
        chargeAmount={chargeAmount}
      />

      <DeleteDraftModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleDeleteDraft(selectedRequest?._id)}
        isLoading={isActionLoading}
      />
    </Layout>
  );
};

export default AdminModificationHistoryDetailPage;
